import {createFileRoute} from '@tanstack/react-router';

import type {ListScope} from '@/lib/list-scope';
import {parseNonEmpty, parsePage} from '@/lib/search-params';
import {JobsListPage} from '@/modules/jobs/pages/JobsListPage';
import {JOB_STATUS_ORDER, type JobStatus} from '@/modules/jobs/lib/status';
import {parseCrewKind, type CrewKind} from '@/modules/jobs/lib/crew-kind';

/** The three list-surface views behind the toolbar toggle (built in F2/F3). */
export type JobsView = 'list' | 'board' | 'schedule';

const VIEW_VALUES: readonly JobsView[] = ['list', 'board', 'schedule'];

/**
 * URL state for the jobs index. All fields optional so links from elsewhere
 * (dashboard, detail) can land here bare; the page reads defaults
 * (`view ?? "list"`, `page ?? 1`). This contract is consumed by the real
 * list/board/schedule build in F2 (RE-1864) / F3 (RE-1865).
 */
export interface JobsListSearch {
  view?: JobsView;
  /** Everyone / Mine, as on the advance and reimbursement lists. */
  scope?: ListScope;
  status?: JobStatus;
  project?: string;
  site?: string;
  assignee?: string;
  /** Jobs with staff on them, or with visitors on them (`jobs:view_all` only). */
  crew?: CrewKind;
  q?: string;
  page?: number;
}

function parseView(value: unknown): JobsView | undefined {
  return VIEW_VALUES.find((v) => v === value);
}

const SCOPE_VALUES: ReadonlyArray<ListScope> = ['all', 'mine'];

function parseScope(value: unknown): ListScope | undefined {
  return SCOPE_VALUES.find((s) => s === value);
}

function parseStatus(value: unknown): JobStatus | undefined {
  return JOB_STATUS_ORDER.find((s) => s === value);
}

const validateJobsListSearch = (search: Record<string, unknown>): JobsListSearch => ({
  view: parseView(search.view),
  scope: parseScope(search.scope),
  status: parseStatus(search.status),
  project: parseNonEmpty(search.project),
  site: parseNonEmpty(search.site),
  assignee: parseNonEmpty(search.assignee),
  crew: parseCrewKind(search.crew),
  q: parseNonEmpty(search.q),
  page: parsePage(search.page),
});

export const Route = createFileRoute('/_authenticated/jobs/')({
  validateSearch: validateJobsListSearch,
  component: JobsListPage,
});
